import { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  message,
  Upload,
  Button,
  Image,
  Col,
  Row,
  Select,
} from "antd";
import { ShowToast, Severty } from "../../helper/toast";
import { uploadFileToS3 } from "../../helper/uploadImage";
import { UploadOutlined } from "@ant-design/icons";
import useApi from "../../hooks/useApi";
import { getBranch, getCities, updateBranch } from "../../constants/API/API";
import callApi from "../../constants/API/CallApi";

const FileType = [
  "image/png",
  "image/jpg",
  "image/jpeg",
  "image/avif",
  "image/webp",
  "image/gif",
];

const BranchUpdate = ({
  editVisible,
  setEditVisible,
  selectedBranch: branchId,
  refresh,
}) => {
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [form] = Form.useForm();
  const [gallery, setGallery] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);

  useApi({
    callback: getBranch,
    payload: branchId._id,
    dependencies: [branchId],
    shouldCallApi: Boolean(branchId),
    onSuccess: (data) => {
      setSelectedBranch(data);
    },
  });

  useApi({
    callback: getCities,
    payload: {},
    onSuccess: ({ list }) => {
      setCities(list || []);
    },
    onError: (er) => {
      console.log(er);
    },
  });

  useEffect(() => {
    if (selectedBranch && editVisible) {
      const { gallery } = selectedBranch;

      const formattedGallery = (gallery || []).map((url, idx) => ({
        uid: `gallery-${idx}`,
        name: `image-${idx}`,
        status: "done",
        url: url,
        thumbUrl: url,
      }));

      setGallery(formattedGallery);
      form.setFieldsValue({
        name: selectedBranch.name,
        city: selectedBranch.city?._id,
        address: selectedBranch.address,
        description: selectedBranch.description,
        gallery: formattedGallery,
      });
    }
  }, [selectedBranch, editVisible]);

  const handleGalleryChange = ({ fileList }) => {
    // Only keep files with uploaded URLs
    setGallery(
      fileList
        .filter(
          (f) =>
            f.status === "done" &&
            (f.url || (f.response && f.response.Location))
        )
        .map((f) => f.url || (f.response && f.response.Location))
    );
  };

  const handleOk = (values) => {
    const formattedGallery = gallery?.[0]?.url
      ? gallery.map((file) => file.url)
      : gallery;
    setLoading(true);
    callApi({
      callback: updateBranch,
      payload: [
        {
          ...values,
          gallery: formattedGallery,
        },
        selectedBranch._id,
      ],
      onSuccess: ({ message }) => {
        ShowToast(message, Severty.SUCCESS);
        refresh(new Date().getTime());
        setEditVisible(false);
        setLoading(false);
      },
      onError: (err) => {
        ShowToast(err, Severty.ERROR);
        setLoading(false);
      },
    });
  };

  const validateName = (_, value) => {
    if (!value) return Promise.reject("Please enter a name");
    if (!/^[A-Z][a-zA-Z ]*$/.test(value))
      return Promise.reject(
        "Name must start with a capital letter and contain only letters"
      );
    return Promise.resolve();
  };

  const beforeUpload = (file) => {
    if (!FileType.includes(file.type)) {
      message.error("Invalid file type");
      return false;
    }
    if (file.size / 1024 / 1024 >= 5) {
      message.error("Image must be smaller than 5 MB");
      return false;
    }
    return true;
  };

  const handleCancel = () => {
    form.resetFields();
    setEditVisible(false);
    setGallery([]);
  };

  return (
    <Modal
      open={editVisible}
      title="Update Branch"
      onCancel={handleCancel}
      width={700}
      afterClose={() => form.resetFields()}
      okButtonProps={{ htmlType: "submit", form: "edit-branch", loading }}
      className="tab_modal"
    >
      <Form id="edit-branch" form={form} layout="vertical" onFinish={handleOk}>
        <Row gutter={[24, 0]}>
          <Col span={24} md={12}>
            <Form.Item
              name="name"
              label="Branch Name"
              rules={[{ validator: validateName }]}
            >
              <Input placeholder="Enter branch name" />
            </Form.Item>
          </Col>
          <Col span={24} md={12}>
            <Form.Item
              name="city"
              label="City"
              rules={[{ required: true, message: "Please enter the city" }]}
            >
              <Select placeholder="Select City">
                {cities.map((city) => (
                  <Select.Option key={city?._id}>{city?.name}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={24} md={12}>
            <Form.Item
              name="address"
              label="Full Address"
              rules={[{ required: true, message: "Please enter the address" }]}
            >
              <Input placeholder="Enter full address" />
            </Form.Item>
          </Col>
          <Col span={24} md={12}>
            <Form.Item
              name="description"
              label="Details"
              rules={[{ required: true, message: "Please enter details" }]}
            >
              <Input placeholder="Enter details" />
            </Form.Item>
          </Col>
          <Col span={24} md={12}>
            <Form.Item
              name="gallery"
              label="Gallery (Multiple Images)"
              valuePropName="fileList"
              getValueFromEvent={(e) => e?.fileList}
            >
              <Upload
                listType="picture"
                multiple
                customRequest={async ({ file, onSuccess }) => {
                  try {
                    const result = await uploadFileToS3({
                      file,
                      dirName: "dojo/branchGallery",
                    });
                    file.url = result.Location;
                    file.thumbUrl = result.Location;
                    onSuccess(file, file);
                  } catch (err) {
                    message.error("Failed to upload image");
                  }
                }}
                beforeUpload={beforeUpload}
                onChange={handleGalleryChange} // <-- Add this
              >
                <Button icon={<UploadOutlined />}>Upload Gallery Images</Button>
              </Upload>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default BranchUpdate;
